import { createContext, FC, useContext, useEffect, useState } from 'react'
import { getAllCommentsResolved } from 'components/QA/Assessments/helpers'
import { useQAAssessmentContext } from './QAAssessmentContext'
import { useQAAssessmentCommentsContext } from './QAAssessmentCommentsContext'
import { NotificationContext, NOTIFY_KEYS } from './NotificationContext'
import { useAssessmentContext } from './AssessmentContext'

interface QAAssessmentActionsContextType {
  createCommentClickHandler: (questionLinkId: string) => void
  disableHover: boolean
}

const QAAssessmentActionsContext = createContext<QAAssessmentActionsContextType>({
  createCommentClickHandler: () => {},
  disableHover: false
})

export const useQAAssessmentActionsContext = (): QAAssessmentActionsContextType => {
  return useContext(QAAssessmentActionsContext)
}

interface QAAssessmentActionsProviderProps {
  children: React.ReactNode
}

export const QAAssessmentActionsProvider: FC<QAAssessmentActionsProviderProps> = ({ children }) => {
  const {
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { qaNurse, caseManager, isFinalRoundQA, qaTask, qaAssessmentItems, actingRole } =
    useQAAssessmentContext()
  const { sortedComments, setFocusedCommentId, addNewComment } = useQAAssessmentCommentsContext()

  const { notify } = useContext(NotificationContext)

  const [isCreatingComment, setIsCreatingComment] = useState(false)

  const canCreateComment = (linkId: string): boolean => {
    if (isFinalRoundQA) {
      return false
    }
    const assessmentItem = qaAssessmentItems && qaAssessmentItems[linkId]

    const matchingComments = sortedComments.filter((c) => c.topic && c.topic.id === linkId)
    const allCommentsAreResolved = getAllCommentsResolved(matchingComments)

    return (
      allCommentsAreResolved &&
      Boolean(inQA && actingRole?.isQANurse) &&
      Boolean(assessmentItem && qaTask && qaNurse && caseManager)
    )
  }

  const createCommentClickHandler = (questionLinkId: string): void => {
    const canCreate = canCreateComment(questionLinkId)
    if (!canCreate || isCreatingComment) return

    setIsCreatingComment(true)

    const createdComment = addNewComment(questionLinkId)

    if (createdComment?.id) {
      setFocusedCommentId(createdComment.id)
    }

    setIsCreatingComment(false)
  }

  useEffect(() => {
    if (isFinalRoundQA) {
      notify.info({
        message: 'Final Round of QA',
        description: 'No more comments can be added to the assessment.',
        key: NOTIFY_KEYS.ASSESSMENT_FINAL_ROUND_QA,
        duration: null
      })
    } else {
      notify.destroy(NOTIFY_KEYS.ASSESSMENT_FINAL_ROUND_QA)
    }
  }, [isFinalRoundQA, notify])

  const disableHover =
    isCreatingComment ||
    isFinalRoundQA ||
    (inQA && !actingRole?.isQANurse) ||
    (inQAEdit && !actingRole?.isCaseManager)

  return (
    <QAAssessmentActionsContext.Provider
      value={{
        createCommentClickHandler,
        disableHover
      }}
    >
      {children}
    </QAAssessmentActionsContext.Provider>
  )
}

export default QAAssessmentActionsContext
